import React, { useEffect } from "react";
import { X } from "lucide-react";

/**
 * ImageLightbox
 * Props:
 * - open: boolean
 * - imageUrl: string | null
 * - alt: string
 * - zoomLevel: number
 * - setZoomLevel: (number | (prev:number)=>number) => void
 * - onClose: () => void
 */
export function ImageLightbox({
  open,
  imageUrl,
  alt,
  zoomLevel,
  setZoomLevel,
  onClose,
}) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      // Close
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      // Zoom in: + or =
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        setZoomLevel((prev) => Math.min(prev + 0.25, 3));
        return;
      }

      // Zoom out: - or _
      if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
        return;
      }

      // Reset zoom: 0
      if (e.key === "0") {
        e.preventDefault();
        setZoomLevel(1);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, setZoomLevel]);

  if (!open || !imageUrl) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black bg-opacity-95 flex items-center justify-center"
      onClick={(e) => {
        // Prevent clicks from closing the underlying modal
        // User must click the X button to close the lightbox
        e.stopPropagation();
      }}
      role="dialog"
      aria-modal="true"
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 bg-white bg-opacity-90 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition-all z-10"
        title="Close (Esc)"
        type="button"
      >
        <X size={24} />
      </button>

      <div className="overflow-auto max-h-screen max-w-screen p-4">
        <img
          src={imageUrl}
          alt={alt}
          className="object-contain transition-transform duration-200"
          style={{
            transform: `scale(${zoomLevel})`,
            transformOrigin: "center center",
            maxHeight: "90vh",
            maxWidth: "90vw",
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-90 px-4 py-2 rounded-lg shadow-lg">
        <p className="text-sm font-semibold text-gray-800">
          Esc to close • + / - to zoom • 0 to reset
        </p>
      </div>
    </div>
  );
}